// Packages
import { createSlice } from '@reduxjs/toolkit';
// Redux
import {
  getCandidatesListAsync,
  createCandidateAsync,
  getCandidateDetailsAsync,
  updateCandidateAsync,
  deleteCandidateAsync,
} from './candidatesThunk';
import { clearReducer } from '../auth/authThunk';
// Interfaces and types
import { IGetCandidatesRes, IGetCandidateRes } from 'types/candidateTypes';
import { IErrorRes } from 'types/appTypes';

interface ISliceCandidatesState {
  candidates: IGetCandidatesRes | null;
  candidateDetails: IGetCandidateRes | null;
  error: IErrorRes | null;
  isLoading: boolean;
}

const initialState: ISliceCandidatesState = {
  candidates: null,
  candidateDetails: null,
  error: null,
  isLoading: false,
};

const candidateSlice = createSlice({
  name: 'candidates',
  initialState,
  reducers: {
    clearErrors: state => {
      state.error = null;
    },
    setCandidateDetails: (state, { payload }) => {
      state.candidateDetails = payload;
    },
  },
  extraReducers: builder => {
    //* GET CANDIDATES ASYNC THUNK
    builder.addCase(getCandidatesListAsync.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getCandidatesListAsync.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.candidates = payload;
    });
    builder.addCase(getCandidatesListAsync.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.error = payload || null;
    });

    //* CREATE CANDIDATE ASYNC THUNK
    builder.addCase(createCandidateAsync.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createCandidateAsync.fulfilled, state => {
      state.isLoading = false;
    });
    builder.addCase(createCandidateAsync.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.error = payload || null;
    });

    //* GET CANDIDATE DETAILS ASYNC THUNK
    builder.addCase(getCandidateDetailsAsync.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getCandidateDetailsAsync.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.candidateDetails = payload;
    });
    builder.addCase(getCandidateDetailsAsync.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.error = payload || null;
    });

    //* UPDATE CANDIDATE (one) ASYNC THUNK
    builder.addCase(updateCandidateAsync.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateCandidateAsync.fulfilled, state => {
      state.isLoading = false;
    });
    builder.addCase(updateCandidateAsync.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.error = payload || null;
    });

    //* DELETE CANDIDATE ASYNC THUNK
    builder.addCase(deleteCandidateAsync.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteCandidateAsync.fulfilled, state => {
      state.isLoading = false;
    });
    builder.addCase(deleteCandidateAsync.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.error = payload || null;
    });

    //* CLEAR REDUCER AFTER SIGN OUT
    builder.addCase(clearReducer, () => initialState);
  },
});

export const { setCandidateDetails } = candidateSlice.actions;

export default candidateSlice;
